import { FC, useCallback, useMemo } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Save } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import { useDictionary } from '@cvt/hooks/useDictionary';

import { useMeetingCrud } from '../hooks/useMeetingCrud';
import { useMeeting } from '../hooks/useMeeting';

import { FormCrud, MeetingForm } from './MeetingForm/MeetingForm';
import { StoriesThroughSection } from './MeetingForm/StoriesThroughSection';


interface ViewProps {
  onSuccess?: (meeting: Meetings.Meeting) => void;
  meetingId: number;
}

export const EditMeetingPartial: FC<ViewProps> = (props) => {
  const { onSuccess, meetingId } = props;
  const router = useRouter();
  const dictionary = useDictionary();

  const { meeting, isFetching } = useMeeting({ id: meetingId });

  const { editMeeting } = useMeetingCrud();

  const onSubmit = useCallback(async (data: FormCrud) => {
    try {
      const response = await editMeeting.mutateAsync(data as Meetings.Edit);

      onSuccess?.(response.data);
    } catch (error) {
      console.error(error);
    }
  }, [editMeeting, onSuccess]);

  const defaultValues: Partial<FormCrud> = useMemo(() => {
    const { user, storiesThrough = [],  ...rest } = meeting || ({} as Meetings.Meeting);
    const hydratedStoriesThrough = storiesThrough.map(storyThrough => {
      const hasAdaptation = typeof storyThrough.meetingAdaptation === 'string' && storyThrough.meetingAdaptation.trim().length > 0;
      return {
        ...storyThrough,
        meetingAdaptation: hasAdaptation ? storyThrough.meetingAdaptation : (storyThrough.story?.content ?? ''),
      };
    });
    return {
      ...rest,
      storiesThrough: hydratedStoriesThrough,
    };
  }, [meeting]);

  return (
    <Stack gap={2} width="100%">

      <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
        <Button variant="outlined" color="primary" onClick={() => router.meetings.go()}>
          Back to Meetings
        </Button>
        <Stack>
          <Typography variant="body1" fontSize={30} fontWeight={600} textAlign={{ xs: 'center', sm: 'left' }}>
            {dictionary.meetings.edit.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize={16} textAlign={{ xs: 'center', sm: 'left' }}>
            {dictionary.meetings.edit.subtitle}
          </Typography>
        </Stack>
      </Stack>

      <MeetingForm
        key={isFetching ? 'loading' : meetingId}
        onSubmitRequest={onSubmit}
        defaultValues={defaultValues}
        slots={{
          storiesThrough: (formState) => (
            <StoriesThroughSection meetingId={meetingId}/>
          ),
          footer: (formState) => (
            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} justifyContent="flex-end" width="100%" gap={2} mt={2}>
              <Button
                variant="outlined"
                color="primary"
                type="button"
                size="large"
                onClick={() => router.meetings.go()}
              >
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                size="large"
                type="submit"
                startIcon={<Save/>}
                loading={formState.isSubmitting}
              >
                Save Meeting
              </LoadingButton>
            </Stack>
          ),
        }}
      />
    </Stack>
  );
};
